import type { AdminLanguage } from '@sirius/contracts/admin'
import {
  credits,
  creditsFromDecimal,
  formatCredits as exactCreditsText,
} from '@sirius/contracts/admin'

// The panel's one formatter: dates, numbers and plural forms follow the panel
// language, not the browser's. The provider (`panelLanguage.tsx`) sets the
// language when it changes; code outside React (column cells, message
// builders) calls these without a hook, so a page shows the new language when
// it renders next.

/** The BCP 47 tag each panel language formats with. */
const INTL_LOCALES: Record<AdminLanguage, string> = {
  en: 'en-US',
  ru: 'ru-RU',
  tr: 'tr-TR',
}

let current: AdminLanguage = 'en'

export function setFormatLanguage(language: AdminLanguage) {
  current = language
}

export function formatLanguage(): AdminLanguage {
  return current
}

export const intlLocale = (language: AdminLanguage = current) =>
  INTL_LOCALES[language]

/** A date and time, as `Date#toLocaleString` writes it in the panel language. */
export function formatDateTime(
  date: Date,
  language: AdminLanguage = current,
): string {
  return date.toLocaleString(intlLocale(language))
}

/** `formatDateTime`, or undefined for no date (a column that was never set). */
export function formatOptionalDateTime(
  date: Date | null | undefined,
  language: AdminLanguage = current,
): string | undefined {
  return date === null || date === undefined
    ? undefined
    : formatDateTime(date, language)
}

const dateFormats = new Map<string, Intl.DateTimeFormat>()

/**
 * A date in the pattern `options` name (fields, `timeZone`, `hourCycle`), in
 * the panel language's order and words: `Jan 8` in English, `8 янв.` in
 * Russian. A formatter per language and options is built once.
 */
export function formatDateParts(
  date: Date,
  options: Intl.DateTimeFormatOptions,
  language: AdminLanguage = current,
): string {
  const locale = intlLocale(language)
  const key = `${locale}|${JSON.stringify(options)}`
  let format = dateFormats.get(key)
  if (format === undefined) {
    format = new Intl.DateTimeFormat(locale, options)
    dateFormats.set(key, format)
  }
  return format.format(date)
}

/** A number with the panel language's grouping and decimal marks. */
export function formatNumber(
  value: number,
  language: AdminLanguage = current,
): string {
  return value.toLocaleString(intlLocale(language))
}

/**
 * The form of a message for `count`, by the panel language's plural rules
 * (`Intl.PluralRules`: English has one and other; Russian also few and many).
 * A category the language has but `forms` lacks falls back to `other`.
 */
export function plural<Message>(
  count: number,
  forms: Partial<Record<Intl.LDMLPluralRule, Message>> & { other: Message },
  language: AdminLanguage = current,
): Message {
  return (
    forms[new Intl.PluralRules(intlLocale(language)).select(count)] ??
    forms.other
  )
}

const creditFormats = new Map<string, Intl.NumberFormat>()

function creditFormat(
  language: AdminLanguage,
  options: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const locale = intlLocale(language)
  const key = `${locale}|${JSON.stringify(options)}`
  let format = creditFormats.get(key)
  if (format === undefined) {
    format = new Intl.NumberFormat(locale, options)
    creditFormats.set(key, format)
  }
  return format
}

/**
 * The exact decimal text of `units` (hundredths of a credit, negative for a
 * debit or a loss) as a numeric string, which `Intl.NumberFormat` formats
 * without turning it into a float.
 * @throws RangeError when `units` is no safe integer.
 */
function decimalText(units: number): `${number}` {
  const text = exactCreditsText(credits(Math.abs(units)))
  return (units < 0 ? `-${text}` : text) as `${number}`
}

/**
 * An amount of credits with two decimals in the panel language (`1,234.50`;
 * `1.234,50` in Turkish), from stored units (hundredths of a credit).
 * Negative amounts keep their sign.
 */
export function formatCredits(
  units: number,
  language: AdminLanguage = current,
): string {
  return creditFormat(language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(decimalText(units))
}

/** `formatCredits` with a plus before a gain (a ledger entry, a profit). */
export function formatSignedCredits(
  units: number,
  language: AdminLanguage = current,
): string {
  return creditFormat(language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay: 'exceptZero',
  }).format(decimalText(units))
}

/**
 * Credits without trailing zeros (`1`, `1.5`, `1.05`), for a chart's axis
 * ticks where `1.00` would only be wider.
 */
export function formatCreditsCompact(
  units: number,
  language: AdminLanguage = current,
): string {
  return creditFormat(language, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(decimalText(units))
}

const CREDITS_TEXT = /^\d+(?:[.,]\d{1,2})?$/

/**
 * What an operator typed as an amount of credits (`0.5`, `0,50`, `100`) as
 * stored units, or undefined when it is no such amount: digits with at most
 * two decimals after a `.` or `,`, no grouping, sign or exponent (`1,000`
 * would be one thousand or one credit). Exact: no float math.
 */
export function parseCredits(text: string): number | undefined {
  const trimmed = text.trim()
  if (!CREDITS_TEXT.test(trimmed)) {
    return undefined
  }
  try {
    return creditsFromDecimal(trimmed.replace(',', '.'))
  } catch {
    return undefined
  }
}
